import React, { useState, useMemo } from 'react';

import { Modal, Steps } from 'antd';
import Axios from 'axios';

import { getTodayISO, showDate, showLog, toISODate } from '@libs/NwMoment';
import { deaccentizeandlowerize } from '@libs/NwUtils';

import { useDeletePackage } from '@hooks/API/Packages/UsePackages';
import { forceReloadProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectTimeline } from '@hooks/API/Project/UseProjectTimeline';
import useDepartments from '@hooks/CachedLists/UseDepartments';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import CreatedBy from '@components/CreatedBy/CreatedBy';
import { NwLinkButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
import PackageEditDrawer from '@components/PackageDrawer/PackageHeader/PackageEditDrawer';
import ProjectDrawer from '@components/Project/ProjectDrawer';
import ProjectSelector from '@components/ProjectSelector/ProjectSelector';

import { usePackageContext } from '../PackageContext';
import PackageCloneDrawer from './PackageCloneDrawer';
import PackageLinkToProjectDrawer from './PackageLinkToProjectDrawer';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const PackageHeader = ({ compact, onClonePackage, onDeletePackage }) => {
    const packageContextValue = usePackageContext();
    const { groupedPackage, afterUpdatePackage, setLoadingZip } = packageContextValue;

    const [editPackage, setEditPackage] = useState(false);
    const [linkPackage, setLinkPackage] = useState(false);
    const [clonePackage, setClonePackage] = useState(false);
    const [openProject, setOpenProject] = useState(false);
    const isExpired = getTodayISO() > toISODate(groupedPackage.ExpirationDate);
    const breakpoints = useNwBreakPoints();
    const { mutateAsync: deletePackage } = useDeletePackage();

    const projectID = groupedPackage.Project ? groupedPackage.Project.ID : null;

    const departments = useDepartments();

    const handleDeletePackage = async () => {
        try {
            await deletePackage({ PackageID: groupedPackage.ID });
            if (projectID) {
                forceReloadProject(projectID);
                forceReloadProjectTimeline(projectID);
            }
            onDeletePackage();
        } catch (e) {
            Modal.error({
                title: e.response.data.Message,
            });
        }
    };

    const departmentName = useMemo(() => {
        if (!groupedPackage) return null;
        if (!groupedPackage.DepartmentCreated) return null;
        const department = departments.find((dept) => dept.id === groupedPackage.DepartmentCreated);
        return department ? department.name : null;
    }, [groupedPackage, departments]);

    const handleClonePackage = async () => {
        setClonePackage(true);
    };

    const handleClosePackageCloneDrawer = (clonedPackageID) => {
        setClonePackage(false);
        if (clonedPackageID) {
            onClonePackage(clonedPackageID);
        }
    };

    const handleUnlinkProject = async () => {
        await Axios.put(`/packages/unlinkfromproject/${groupedPackage.ID}`);
        afterUpdatePackage();
    };

    const handleDownloadZip = async () => {
        setLoadingZip(true);
        const normalizedPackageName = deaccentizeandlowerize(groupedPackage.Name).replace(/\s+/g, '-');
        try {
            const blob = await Axios.post(
                '/packages/zip',
                {
                    Template: 'pla-light',
                    Area: 'Playlist',
                    OutputFileName: null,
                    PackageID: groupedPackage.ID,
                },
                { responseType: 'blob', timeout: 120000 }
            );
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${normalizedPackageName}.zip`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            Modal.error({
                title: e.message || 'Error downloading package',
            });
        } finally {
            setLoadingZip(false);
        }
    };

    let currentStep = 0;
    if (groupedPackage.LastSentDate) {
        currentStep = 1;
    }
    if (groupedPackage.LastSentDate && isExpired) {
        currentStep = 2;
    }

    const getDdMenuActions = () => {
        const menuActions = [
            {
                name: '',
                key: 'edit',
                pinned: true,
                icon: byPrefixAndName.fasl['pen'],
                action: () => setEditPackage(true),
            },
        ];
        if (groupedPackage.Project && groupedPackage.Project.ID) {
            var unlinkaction = {
                name: '',
                type: 'delete',
                tooltip: `unlink this package from the project "${groupedPackage.Project.Name}"`,
                confirmationLabel: `This will unlink the current package from the project "${groupedPackage.Project.Name}". Please confirm your choice`,
                confirmationButtonLabel: 'unlink',
                confirmationButtonIcon: byPrefixAndName.fasl['link-slash'],
                confirmationPlacement: 'bottomLeft',
                pinned: true,
                icon: byPrefixAndName.fasl['folder-xmark'],
                key: 'unlink',
                action: () => handleUnlinkProject(),
            };
            menuActions.push(unlinkaction);
        } else {
            menuActions.push({
                name: '',
                key: 'link-project',
                tooltip: 'link this package to a project',
                pinned: true,
                icon: byPrefixAndName.fasl['folder-plus'],
                action: () => setLinkPackage(true),
            });
        }
        if (!compact) {
            menuActions.push({
                name: '',
                key: 'clone',
                tooltip: 'clone this package',
                pinned: true,
                icon: byPrefixAndName.fasl['clone'],
                action: () => handleClonePackage(),
            });
        }
        menuActions.push({
            name: '',
            key: 'download-zip',
            tooltip: 'download package as zip',
            pinned: true,
            icon: byPrefixAndName.fasl['file-zip'],
            action: () => handleDownloadZip(),
        });
        menuActions.push({
            name: '',
            key: 'delete',
            pinned: true,
            tooltip: 'delete this package',
            icon: byPrefixAndName.fasl['trash-alt'],
            action: () => handleDeletePackage(),
            type: 'delete',
            confirmationPlacement: 'left',
        });
        return menuActions;
    };

    return (
        <div className="package-drawer-header-container">
            <div className="title-card-wrapper">
                <NwCard
                    title={groupedPackage.Name}
                    icon={byPrefixAndName.fasl['book-arrow-right']}
                    extra={<NwDropDownMenu actions={getDdMenuActions()} />}
                >
                    <div className="package-header">
                        {groupedPackage.Project && groupedPackage.Project.ID && (
                            <div className="project-selector-wrapper">
                                <ProjectSelector
                                    small
                                    noBorder
                                    readOnly
                                    projectData={groupedPackage.Project}
                                    value={groupedPackage.Project.ID}
                                />
                                <NwLinkButton
                                    icon={byPrefixAndName.fasl['sidebar-flip']}
                                    primary
                                    onClick={() => setOpenProject(true)}
                                    label="Open Project"
                                />
                            </div>
                        )}
                        <div className="steps-wrapper">
                            <Steps
                                direction={breakpoints > 2 ? 'horizontal' : 'vertical'}
                                current={currentStep}
                                size="small"
                                items={[
                                    {
                                        title: 'Created',
                                        description: (
                                            <div>
                                                <CreatedBy object={groupedPackage} extra="" />
                                                <small style={{ textTransform: 'uppercase' }}>
                                                    department: {departmentName}
                                                </small>
                                            </div>
                                        ),
                                        icon: <NwIcon icon={byPrefixAndName.fasl['plus-circle']} />,
                                    },
                                    {
                                        title: groupedPackage.LastSentDate
                                            ? `Sent to ${groupedPackage.Recipients.length} recipients`
                                            : `Not sent yet`,
                                        description: groupedPackage.LastSentDate
                                            ? `on ${showLog(groupedPackage.LastSentDate)}`
                                            : `${groupedPackage.Models.length} models, ${groupedPackage.Recipients.length} recipients`,
                                        icon: <NwIcon icon={byPrefixAndName.fasl['paper-plane']} />,
                                    },
                                    {
                                        title: isExpired ? 'Expired' : 'Will Expire',
                                        description: `on ${showDate(groupedPackage.ExpirationDate)}`,
                                        icon: (
                                            <NwIcon
                                                icon={
                                                    isExpired
                                                        ? byPrefixAndName.fasl['calendar-times']
                                                        : byPrefixAndName.fasl['calendar-exclamation']
                                                }
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    {editPackage && <PackageEditDrawer onClose={() => setEditPackage(false)} />}
                    {linkPackage && <PackageLinkToProjectDrawer onClose={() => setLinkPackage(false)} />}
                    {clonePackage && (
                        <PackageCloneDrawer
                            packageName={groupedPackage.Name}
                            packageID={groupedPackage.ID}
                            onClose={handleClosePackageCloneDrawer}
                        />
                    )}
                    {openProject && (
                        <ProjectDrawer projectId={groupedPackage.Project.ID} onClose={() => setOpenProject(false)} />
                    )}
                </NwCard>
            </div>
        </div>
    );
};

export default PackageHeader;
